import { useCallback, useMemo } from 'react'
import {
  CellClickedEvent,
  ColDef,
  RowNode,
  SuppressKeyboardEventParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community'
import { WbsItemStatus } from '../../../../domain/entity/WbsItemEntity'
import { intl } from '../../../../i18n'
import { ProjectPlanCumulation } from '../../../../lib/functions/projectPlan'
import { WbsItemRow } from '../../../../lib/functions/wbsItem'
import Workload, { WorkloadUnit } from '../../../../lib/functions/workload'
import store from '../../../../store'
import DateVO from '../../../../vo/DateVO'
import { DefaultCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer'
import { FlagxsColumnDef } from '../../../containers/BulkSheetView/gridOptions/extension'
import { ColumnType, defaultColDef } from '../../../containers/commons/AgGrid'
import { useExperiment } from '../../../hooks/useExperiment'
import { featureFlagService } from '../../../../utils/feature-flag'
import {
  useCreatedAt,
  useCreatedBy,
  useRevision,
  useUpdatedAt,
  useUpdatedBy,
} from '../../../page-properties/bulksheet-properties/changeLog'
import {
  useAccountable,
  useActualEndDate,
  useActualStartDate,
  useAssignee,
  useAttachments,
  useCode,
  useLatestComment,
  useCommentSummaryAction,
  useDescription,
  useDifficulty,
  useDisplayName,
  useEstimatedWorkLoadDeliverable,
  useParentWbsItem,
  usePath,
  usePriority,
  useResponsible,
  useScheduledEndDate,
  useScheduledStartDate,
  useSprint,
  useStatus,
  useStatusV2,
  useSubstatus,
  useTags,
  useTeam,
  useTicketType,
  useType,
  useWatchers,
  useEstimatedWorkLoadTask,
  useEstimatedStoryPoint,
  useUuid,
  useEstimatedProgressRate,
  useProgressRate,
  usePlannedValue,
  useEarnedValue,
  usePreceding,
  useDelayed,
  useRemaining,
  useActualHour,
  useCostVariance,
  useScheduleVariance,
  useSchedulePerformanceIndex,
  useCostPerformanceIndex,
  useEstimateToComplete,
  useEstimateAtCompletion,
  useVarianceAtCompletion,
} from '../../../page-properties/bulksheet-properties/wbsItem'
import {
  commonValueSetter,
  defaultValueSetter,
} from '../../../page-properties/bulksheet-properties'
import { WbsItemSearchRow } from '../wbsItemSearch'
import { DEFAULT_CELL_CLASS_RULES } from '../../../containers/BulkSheetView/gridOptions/cell/cellClassRules'

export const COLUMN_GROUP_UPDATE_INFO_EXTERNAL_ID: string =
  'wbsItem.search.updateInfo'
export const FIELD_ATTCHMENT = 'deliverableAttachmentSummary'
export const FIELD_PATH_COL: string = 'path'
export const FIELD_PARENT_WBS_ITEM_COL: string = 'parentWbsItem'
export const FIELD_WBSITEM_ACTUALDATE_START = 'wbsItem.actualDate.startDate'
export const FIELD_WBSITEM_ACTUALDATE_END = 'wbsItem.actualDate.endDate'
const FIELD_SPRINT: string = 'wbsItem.sprint'

const statusCombinedValuePath = (combinedEnumCode: string) => {
  switch (combinedEnumCode) {
    case 'WBS_TYPE':
      return 'wbsItem.type'
    case 'TICKET_TYPE':
      return 'wbsItem.ticketType'
    case 'WBS_STATUS':
      return 'wbsItem.status'
    case 'WBS_SUBSTATUS':
      return 'wbsItem.substatus'
  }
  return undefined
}

/**
 * WbsItemSearch Common functions
 */
const formatWorkload = (value: number | undefined, denominator: number = 1) =>
  Number((value || 0) / denominator)

const getDeliverableEstimatedHour = (
  data: WbsItemSearchRow | undefined
): number => {
  const wbsItem: WbsItemRow | undefined = data?.wbsItem
  if (wbsItem?.wbsItemType?.isTask()) {
    return 0
  }
  if (wbsItem?.wbsItemType?.isDeliverable()) {
    return wbsItem?.estimatedWorkload?.hour || 0
  }
  const cumulation: ProjectPlanCumulation | undefined = data?.cumulation
  return cumulation
    ? cumulation.sumDeliverableEstimatedHour -
        cumulation.sumDeliverableEstimatedHourDiscard
    : 0
}

const getTaskEstimatedHour = (data: WbsItemSearchRow | undefined): number => {
  const wbsItem: WbsItemRow | undefined = data?.wbsItem
  if (wbsItem?.wbsItemType?.isTask()) {
    return wbsItem?.estimatedWorkload?.hour || 0
  }
  const cumulation: ProjectPlanCumulation | undefined = data?.cumulation
  if (!cumulation) return 0
  if (wbsItem?.wbsItemType?.isDeliverable()) {
    return (
      cumulation.sumTaskEstimatedHourOfDirectChildren -
      cumulation.sumTaskEstimatedHourDiscardOfDirectChildren
    )
  }
  return (
    cumulation.sumTaskEstimatedHour - cumulation.sumTaskEstimatedHourDiscard
  )
}

const estimatedWorkLoadValueSetter = (
  params: ValueSetterParams<WbsItemSearchRow>
) => {
  return commonValueSetter(
    params,
    (params: ValueSetterParams<WbsItemSearchRow>) => {
      const unit = params.context.workloadUnit
      const { dailyWorkHours, monthlyWorkDays } =
        store.getState().tenant.organization!
      let workload: Workload
      switch (unit) {
        case WorkloadUnit.MONTH:
          workload = Workload.from({
            month: Number(params.newValue),
            standard: { dailyWorkHours, monthlyWorkDays },
          })
          break
        case WorkloadUnit.DAY:
          workload = Workload.from({
            day: Number(params.newValue),
            standard: { dailyWorkHours, monthlyWorkDays },
          })
          break
        case WorkloadUnit.HOUR:
          workload = Workload.from({
            hour: Number(params.newValue),
            standard: { dailyWorkHours, monthlyWorkDays },
          })
          break
        default:
          workload = new Workload(0, 0, 0, {
            dailyWorkHours,
            monthlyWorkDays,
          })
      }
      params.data.wbsItem.estimatedWorkload = workload
    }
  )
}

/*
 * Default Column Def
 */
export const DEFAULT_COLUMN_DEF: ColDef = {
  ...defaultColDef({ editable: false, sortable: true }),
  cellRenderer: DefaultCellRenderer,
  valueSetter: defaultValueSetter,
  suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
    // When editing in Japanese, any keyboard event should be suppressed.
    if (params.editing && params.event.isComposing) {
      return true
    }
    // Prevent editing cell for DELETE or BACKSPACE
    return !params.editing && ['Delete', 'Backspace'].includes(params.event.key)
  },
  cellClassRules: DEFAULT_CELL_CLASS_RULES,
}

/*
 * WbsItemSearch Column Def
 */
export interface PropsForGetColumnDefs {
  projectUuid: string
  selectColumnOptionMap: {
    [key: string]: any
  }
  onClickedStatusCell?: (
    target: EventTarget | undefined,
    row: WbsItemSearchRow | undefined
  ) => void
  onClickedActualHourCell?: (row: WbsItemSearchRow | undefined) => void
  reloadSingleRow?: ((uuid: string) => void) | undefined
  submitSingleRow?: (uuid: string) => Promise<void>
}

export const useColumnDefs = ({
  selectColumnOptionMap,
  onClickedStatusCell,
  onClickedActualHourCell,
  reloadSingleRow,
  submitSingleRow,
}: PropsForGetColumnDefs): FlagxsColumnDef[] => {
  // 実験設定: ステータス列レイアウト
  const isStatusCellNewEnabled = featureFlagService.isEnabled(
    'wbs-item-status-cell-new'
  )
  const { useNew: useNewStatusColumnLayout } = useExperiment(
    'wbs-status-column-layout',
    isStatusCellNewEnabled
  )

  const uuidCol = useUuid()

  const getWbsItemUuidFromNode = useCallback(
    (node: RowNode<WbsItemSearchRow>) => node?.data?.wbsItem?.uuid,
    []
  )
  const getWbsItem = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem,
    []
  )
  const hasWbsItem = useCallback(
    (row: WbsItemSearchRow | undefined) => !!row?.wbsItem?.uuid,
    []
  )
  const getWbsItemType = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem?.wbsItemType,
    []
  )
  const getBaseWbsItemType = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem?.baseWbsItemType,
    []
  )
  const getTeamUuid = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem?.team?.uuid,
    []
  )
  const getScheduledDate = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem?.scheduledDate,
    []
  )
  const getActualDate = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem?.actualDate,
    []
  )
  const getWbsItemStatus = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem?.status,
    []
  )
  const getWbsItemProjectUuid = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.wbsItem?.projectUuid,
    []
  )

  const getCumulation = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.cumulation,
    []
  )
  const getCommentSummary = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.commentSummary,
    []
  )
  const getAttachmentSummary = useCallback(
    (row: WbsItemSearchRow) => row?.deliverableAttachmentSummary,
    []
  )
  const getTypeIndex = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.typeIndex,
    []
  )
  const getTicketTypeIndex = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.ticketTypeIndex,
    []
  )
  const getParentWbsItem = useCallback(
    (row: WbsItemSearchRow | undefined) => row?.parentWbsItem,
    []
  )

  const clearSprint = useCallback(
    (params: ValueSetterParams<WbsItemSearchRow>) => {
      if (!params.data?.wbsItem?.sprint) return
      if (!params.data.editedData) {
        params.data.editedData = {
          [FIELD_SPRINT]: params.data.wbsItem.sprint,
        }
      } else if (!params.data.editedData.hasOwnProperty(FIELD_SPRINT)) {
        params.data.editedData[FIELD_SPRINT] = params.data.wbsItem.sprint
      }
      params.data.wbsItem.sprint = undefined
    },
    []
  )

  // Basic information group column common def
  const codeCol = useCode('wbsItem.code')
  const typeCol = useType({
    field: 'wbsItem.type',
    getBaseWbsItemType,
    getTypeIndex,
  })
  const ticketTypeCol = useTicketType({
    field: 'wbsItem.ticketType',
    getBaseWbsItemType,
    getTypeIndex: getTicketTypeIndex,
  })

  const statusColProduction = useStatus({
    field: 'wbsItem.status',
    subStatusField: 'wbsItem.substatus',
    statusCombinedValuePath,
  })

  const statusColDevelopment = useStatusV2({
    field: 'wbsItem.status',
    subStatusField: 'wbsItem.substatus',
    statusCombinedValuePath,
    onMoreOptionsClick: (
      target: EventTarget | undefined,
      row: WbsItemSearchRow | undefined
    ) => {
      if (!onClickedStatusCell) return
      onClickedStatusCell(target, row)
    },
  })

  const substatusCol = useSubstatus({
    field: 'wbsItem.substatus',
    statusCombinedValuePath,
  })
  const actionCol = useCommentSummaryAction({
    field: 'action',
    getCommentSummary,
    getWbsItem,
  })
  const attachmentsCol = useAttachments({
    field: FIELD_ATTCHMENT,
    getWbsItemUuid: getWbsItemUuidFromNode,
    getAttachmentSummary,
  })
  const tagsCol = useTags({
    field: 'wbsItem.tags',
    getWbsItemProjectUuid,
  })

  // Path group column common def
  const pathCol = usePath(FIELD_PATH_COL)
  const parentWbsItemCol = useParentWbsItem({
    field: FIELD_PARENT_WBS_ITEM_COL,
    getParentWbsItem,
  })

  // Name group column common def
  const onCloseWbsItemDetail = useCallback(
    (wbsItem: WbsItemSearchRow) =>
      wbsItem?.uuid && reloadSingleRow?.(wbsItem.uuid),
    [reloadSingleRow]
  )
  const beforeOpenWbsItemDetail = useCallback(
    async (uuid: string) => {
      submitSingleRow && submitSingleRow(uuid)
      return true
    },
    [submitSingleRow]
  )

  const displayNameColCellRendererParams = useMemo(
    () => ({
      beforeOpenWbsItemDetail,
      onCloseWbsItemDetail,
    }),
    [beforeOpenWbsItemDetail, onCloseWbsItemDetail]
  )
  const displayNameCol = useDisplayName({
    field: 'wbsItem.displayName',
    wbsItemCellRendererParams: displayNameColCellRendererParams,
  })
  const priorityCol = usePriority({
    field: 'wbsItem.priority',
    customEnumOptions: selectColumnOptionMap['priority'],
  })
  const difficultyCol = useDifficulty({
    field: 'wbsItem.difficulty',
    customEnumOptions: selectColumnOptionMap['difficulty'],
  })

  // Explanation group column common def
  const descriptionCol = useDescription('wbsItem.description')
  const latestCommentCol = useLatestComment('commentSummary.latestComment')

  // Assignee group column common def
  const teamCol = useTeam({
    field: 'wbsItem.team',
    gridOptionContext: selectColumnOptionMap,
    clearSprint,
  })
  const accountableCol = useAccountable({
    field: 'wbsItem.accountable',
  })
  const responsibleCol = useResponsible({
    field: 'wbsItem.responsible',
  })
  const assigneeCol = useAssignee({
    field: 'wbsItem.assignee',
  })
  const watchersCol = useWatchers({
    field: 'wbsItem.watchers',
  })

  // Estimated group column common def
  const estimatedWorkLoadDeliverableCol = useEstimatedWorkLoadDeliverable({
    field: 'wbsItem.estimatedWorkload.deliverable',
    getWbsItemType,
  })
  const estimatedWorkLoadTaskCol = useEstimatedWorkLoadTask({
    field: 'wbsItem.estimatedWorkload.task',
    getWbsItemType,
  })
  const estimatedStoryPoint = useEstimatedStoryPoint(
    'wbsItem.estimatedStoryPoint'
  )

  // Progress group column common def
  const estimatedProgressRateCol = useEstimatedProgressRate(
    'estimatedProgressRate',
    getWbsItem,
    getCumulation
  )
  const progressRateCol = useProgressRate(
    'progressRate',
    getWbsItem,
    getCumulation
  )
  const plannedValueCol = usePlannedValue(
    'plannedValue',
    getWbsItem,
    getCumulation
  )
  const earnedValueCol = useEarnedValue(
    'earnedValue',
    getWbsItem,
    getCumulation
  )
  const precedingCol = usePreceding('preceding', getWbsItem, getCumulation)
  const delayedCol = useDelayed('delayedWorkload', getWbsItem, getCumulation)
  const remainingCol = useRemaining(
    'remainingWorkload',
    getWbsItem,
    getCumulation
  )

  // Productivity group column common def
  const actualHourCol = useActualHour({
    field: 'wbsItem.actualHour',
    getWbsItemType: getBaseWbsItemType,
    getCumulation,
    getWbsItem,
  })
  const costVarianceCol = useCostVariance(
    'costVariance',
    getWbsItem,
    getCumulation
  )
  const costPerformanceIndexCol = useCostPerformanceIndex(
    'costPerformanceIndex',
    getWbsItem,
    getCumulation
  )
  const scheduleVarianceCol = useScheduleVariance(
    'scheduleVariance',
    getWbsItem,
    getCumulation
  )
  const schedulePerformanceIndexCol = useSchedulePerformanceIndex(
    'schedulePerformanceIndex',
    getWbsItem,
    getCumulation
  )
  const estimateToCompleteCol = useEstimateToComplete(
    'estimateToComplete',
    getWbsItem,
    getCumulation
  )
  const estimateAtCompletionCol = useEstimateAtCompletion(
    'estimateAtCompletion',
    getWbsItem,
    getCumulation
  )
  const varianceAtCompletionCol = useVarianceAtCompletion(
    'varianceAtCompletion',
    getWbsItem,
    getCumulation
  )

  // Term group column common def
  const sprintCol = useSprint({
    field: FIELD_SPRINT,
    getWbsItemProjectUuid,
    getTeamUuid,
  })

  const setScheduledEndDate = useCallback(
    (
      params: ValueSetterParams<WbsItemSearchRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data?.wbsItem?.scheduledDate) {
        params.data.wbsItem.scheduledDate = {}
      }
      params.data.wbsItem.scheduledDate.endDate = newValue
      if (!params.data.editedData) {
        params.data.editedData = {}
      }
      params.data.editedData['wbsItem.scheduledDate.endDate'] = oldValue
    },
    []
  )
  const scheduledStartDateCol = useScheduledStartDate({
    field: 'wbsItem.scheduledDate.startDate',
    getScheduledDate,
    setScheduledEndDate,
  })

  const setScheduledStartDate = useCallback(
    (
      params: ValueSetterParams<WbsItemSearchRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data?.wbsItem?.scheduledDate) {
        params.data.wbsItem.scheduledDate = {}
      }
      params.data.wbsItem.scheduledDate.startDate = newValue
      if (!params.data.editedData) {
        params.data.editedData = {}
      }
      params.data.editedData['wbsItem.scheduledDate.startDate'] = oldValue
    },
    []
  )
  const scheduledEndDateCol = useScheduledEndDate({
    field: 'wbsItem.scheduledDate.endDate',
    getScheduledDate,
    setScheduledStartDate,
  })

  const setActualEndDate = useCallback(
    (
      params: ValueSetterParams<WbsItemSearchRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data?.wbsItem?.actualDate) {
        params.data.wbsItem.actualDate = {}
      }
      params.data.wbsItem.actualDate.endDate = newValue
      if (!params.data.editedData) {
        params.data.editedData = {}
      }
      params.data.editedData[FIELD_WBSITEM_ACTUALDATE_END] = oldValue
    },
    []
  )
  const actualStartDateCol = useActualStartDate({
    field: FIELD_WBSITEM_ACTUALDATE_START,
    getWbsItemStatus,
    getScheduledDate,
    getActualDate,
    setActualEndDate,
  })

  const setActualStartDate = useCallback(
    (
      params: ValueSetterParams<WbsItemSearchRow>,
      newValue: string,
      oldValue: string
    ) => {
      if (!params.data?.wbsItem?.actualDate) {
        params.data.wbsItem.actualDate = {}
      }
      params.data.wbsItem.actualDate.startDate = newValue
      if (!params.data.editedData) {
        params.data.editedData = {}
      }
      params.data.editedData[FIELD_WBSITEM_ACTUALDATE_START] = oldValue
    },
    []
  )
  const actualEndDateCol = useActualEndDate({
    field: FIELD_WBSITEM_ACTUALDATE_END,
    getWbsItemStatus,
    getScheduledDate,
    getActualDate,
    setActualStartDate,
  })

  // Update info group column common def
  const revisionCol = useRevision('revision')
  const createdByCol = useCreatedBy('createdBy')
  const createdAtCol = useCreatedAt('createdAt')
  const updatedByCol = useUpdatedBy('updatedBy')
  const updatedAtCol = useUpdatedAt('updatedAt')
  const statusCol = useNewStatusColumnLayout
    ? statusColDevelopment
    : statusColProduction
  const columnDefs = useMemo(
    () => [
      {
        externalId: 'uuid',
        ...uuidCol,
      },
      {
        externalId: 'rowNumber',
        field: 'rowNumber',
        type: [ColumnType.wbsItemSequenceNo],
        valueGetter: (params: ValueGetterParams<WbsItemSearchRow>) => {
          return (params?.node?.rowIndex ?? 0) + 1
        },
      },
      // Basic information
      {
        externalId: 'wbsItem.search.basic',
        headerName: intl.formatMessage({ id: 'projectPlan.information' }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.code',
            ...codeCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.type',
            ...typeCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.ticketType',
            ...ticketTypeCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.status',
            ...statusCol,
            ...(useNewStatusColumnLayout
              ? {}
              : {
                  onCellClicked: (e: CellClickedEvent<WbsItemSearchRow>) => {
                    if (!onClickedStatusCell) return
                    onClickedStatusCell(e.event?.target ?? undefined, e.data)
                  },
                }),
          },
          {
            externalId: 'wbsItem.search.wbsItem.substatus',
            ...substatusCol,
          },
          {
            externalId: 'wbsItem.search.action',
            ...actionCol,
            lockPosition: false,
          },
          {
            externalId: 'wbsItem.search.attachment',
            ...attachmentsCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.tags',
            ...tagsCol,
          },
        ] as FlagxsColumnDef[],
      },
      // Path
      {
        externalId: 'wbsItem.search.path',
        headerName: intl.formatMessage({ id: 'wbsItemSearch.path' }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.path',
            ...pathCol,
          },
          {
            externalId: 'wbsItem.search.parentWbsItem.displayName',
            ...parentWbsItemCol,
          },
        ] as FlagxsColumnDef[],
      },
      // Name
      {
        externalId: 'wbsItem.search.name',
        headerName: intl.formatMessage({ id: 'wbsItemSearch.name' }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.displayName',
            ...displayNameCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.priority',
            ...priorityCol,
            width: 65,
          },
          {
            externalId: 'wbsItem.search.wbsItem.difficulty',
            ...difficultyCol,
            width: 65,
            hide: true,
          },
        ] as FlagxsColumnDef[],
      },
      // Explanation
      {
        externalId: 'wbsItem.search.explanation',
        headerName: intl.formatMessage({
          id: 'projectPlan.description.comment',
        }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.description',
            ...descriptionCol,
            width: 165,
          },
          {
            externalId: 'wbsItem.search.commentSummary.latestComment',
            ...latestCommentCol,
            width: 235,
          },
        ] as FlagxsColumnDef[],
      },
      // Assignment
      {
        externalId: 'wbsItem.search.assignment',
        headerName: intl.formatMessage({ id: 'projectPlan.assignment' }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.team',
            ...teamCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.accountable',
            ...accountableCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.responsible',
            ...responsibleCol,
          },
          {
            externalId: 'ticket.list.wbsItem.assignee',
            ...assigneeCol,
          },
          {
            externalId: 'ticket.list.wbsItem.watchers',
            ...watchersCol,
          },
        ] as FlagxsColumnDef[],
      },
      // Estimated
      {
        externalId: 'wbsItem.search.estimate',
        headerName: intl.formatMessage({ id: 'projectPlan.estimated' }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.estimatedWorkload.deliverable',
            ...estimatedWorkLoadDeliverableCol,
            valueSetter: estimatedWorkLoadValueSetter,
            valueGetter: (params: ValueGetterParams<WbsItemSearchRow>) => {
              const data: WbsItemSearchRow | undefined = params.data
              if (!data || data.wbsItem?.wbsItemType?.isTask()) {
                return undefined
              }
              const deliverableEstimatedHour = getDeliverableEstimatedHour(data)
              return formatWorkload(
                deliverableEstimatedHour,
                params.context.workloadUnitState?.hoursPerSelectedUnit || 1
              )
            },
          },
          {
            externalId: 'wbsItem.search.wbsItem.estimatedWorkload.task',
            ...estimatedWorkLoadTaskCol,
            valueSetter: estimatedWorkLoadValueSetter,
            valueGetter: (params: ValueGetterParams<WbsItemSearchRow>) => {
              const data: WbsItemSearchRow | undefined = params.data
              const taskEstimatedHour = getTaskEstimatedHour(data)
              return formatWorkload(
                taskEstimatedHour,
                params.context.workloadUnitState?.hoursPerSelectedUnit || 1
              )
            },
          },
          {
            externalId: 'wbsItem.search.wbsItem.estimatedStoryPoint',
            ...estimatedStoryPoint,
          },
        ] as FlagxsColumnDef[],
      },
      // Progress
      {
        externalId: 'wbsItem.search.progress',
        headerName: intl.formatMessage({ id: 'projectOverview.progress' }),
        children: [
          {
            externalId: 'wbsItem.search.estimatedProgressRate',
            ...estimatedProgressRateCol,
          },
          {
            externalId: 'wbsItem.search.progressRate',
            ...progressRateCol,
          },
          {
            externalId: 'wbsItem.search.plannedValue',
            ...plannedValueCol,
          },
          {
            externalId: 'wbsItem.search.earnedValue',
            ...earnedValueCol,
          },
          {
            externalId: 'wbsItem.search.preceding',
            ...precedingCol,
          },
          {
            externalId: 'wbsItem.search.delayedWorkload',
            ...delayedCol,
          },
          {
            externalId: 'wbsItem.search.remainingWorkload',
            ...remainingCol,
          },
        ] as FlagxsColumnDef[],
      },
      // Productivity
      {
        externalId: 'ticket.list.productivity',
        headerName: intl.formatMessage({ id: 'projectOverview.evm' }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.actualHour',
            ...actualHourCol,
            onCellClicked: (e: CellClickedEvent<WbsItemSearchRow>) => {
              if (
                !e.value ||
                e.value === 0.0 ||
                !e.data?.wbsItem?.baseWbsItemType?.isTask() ||
                !onClickedActualHourCell
              ) {
                return
              }
              onClickedActualHourCell(e.data)
            },
          },
          {
            externalId: 'wbsItem.search.costVariance',
            ...costVarianceCol,
          },
          {
            externalId: 'wbsItem.search.costPerformanceIndex',
            ...costPerformanceIndexCol,
          },
          {
            externalId: 'wbsItem.search.scheduleVariance',
            ...scheduleVarianceCol,
          },
          {
            externalId: 'wbsItem.search.schedulePerformanceIndex',
            ...schedulePerformanceIndexCol,
          },
          {
            externalId: 'wbsItem.search.estimateToComplete',
            ...estimateToCompleteCol,
          },
          {
            externalId: 'wbsItem.search.estimateAtCompletion',
            ...estimateAtCompletionCol,
          },
          {
            externalId: 'wbsItem.search.varianceAtCompletion',
            ...varianceAtCompletionCol,
          },
        ] as FlagxsColumnDef[],
      },
      // Term
      {
        externalId: 'wbsItem.search.term',
        headerName: intl.formatMessage({ id: 'projectPlan.term' }),
        children: [
          {
            externalId: 'wbsItem.search.wbsItem.sprint',
            ...sprintCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.scheduledDate.startDate',
            ...scheduledStartDateCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.scheduledDate.endDate',
            ...scheduledEndDateCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.actualDate.startDate',
            ...actualStartDateCol,
          },
          {
            externalId: 'wbsItem.search.wbsItem.actualDate.endDate',
            ...actualEndDateCol,
          },
        ] as FlagxsColumnDef[],
      },
      // Update info
      {
        externalId: 'wbsItem.search.updateInfo',
        headerName: intl.formatMessage({ id: 'changeLog' }),
        children: [
          {
            externalId: 'wbsItem.search.revision',
            ...revisionCol,
          },
          {
            externalId: 'wbsItem.search.createdBy',
            ...createdByCol,
          },
          {
            externalId: 'wbsItem.search.createdAt',
            ...createdAtCol,
          },
          {
            externalId: 'wbsItem.search.updatedBy',
            ...updatedByCol,
          },
          {
            externalId: 'wbsItem.search.updatedAt',
            ...updatedAtCol,
          },
        ] as FlagxsColumnDef[],
      },
    ],
    [
      uuidCol,
      codeCol,
      typeCol,
      ticketTypeCol,
      statusCol,
      substatusCol,
      actionCol,
      attachmentsCol,
      tagsCol,
      pathCol,
      parentWbsItemCol,
      displayNameCol,
      priorityCol,
      difficultyCol,
      descriptionCol,
      latestCommentCol,
      teamCol,
      accountableCol,
      responsibleCol,
      assigneeCol,
      watchersCol,
      estimatedWorkLoadDeliverableCol,
      estimatedWorkLoadTaskCol,
      estimatedStoryPoint,
      estimatedProgressRateCol,
      progressRateCol,
      plannedValueCol,
      earnedValueCol,
      precedingCol,
      delayedCol,
      remainingCol,
      actualHourCol,
      costVarianceCol,
      costPerformanceIndexCol,
      scheduleVarianceCol,
      schedulePerformanceIndexCol,
      estimateToCompleteCol,
      estimateAtCompletionCol,
      varianceAtCompletionCol,
      sprintCol,
      scheduledStartDateCol,
      scheduledEndDateCol,
      actualStartDateCol,
      actualEndDateCol,
      revisionCol,
      createdByCol,
      createdAtCol,
      updatedByCol,
      updatedAtCol,
      onClickedStatusCell,
      onClickedActualHourCell,
    ]
  )
  return columnDefs
}
